/* ============================================================
   WeLume 彫刻オプション（商品ページ）
   - 彫刻の選択（なし/1列/2列）に応じて彫る文字の入力欄を出し分け
   - 選択に応じて隠しproperty「_彫刻料金ID」を同期（料金はカート側が一元管理）
   - シミュレーターと同じライブプレビュー＋文字幅による自動制限
     （書体：Lato / Dancing Script。基準文字列の幅を上限に、超過分は自動カット）
   ============================================================ */
(function () {
  'use strict';

  var SETTINGS = {
    REFERENCE_STRINGS: ['Yuki & Ren', '2026.06.25'], // 上限の基準（両書体で実測し最大幅を採用）
    BASE_FONT_PX: 56,     // 測定の基準サイズ
    DISPLAY_FONT_PX: 34,  // 商品ページでの表示サイズ（比率は保ったまま縮小表示）
    SAFETY_FACTOR: 1.0
  };

  function fontStack(f) {
    return f === 'script' ? "'Dancing Script','Lato',cursive" : "'Lato',sans-serif";
  }

  class WeLumeEngraving extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.text1 = this.querySelector('.wl-eng__text--1');
      this.text2 = this.querySelector('.wl-eng__text--2');
      this.feeInput = this.querySelector('[data-eng-feeid]');

      // プレビュー要素
      this.preview = this.querySelector('.wl-eng__preview');
      this.plate = this.querySelector('.wl-eng__plate');
      this.cap = this.querySelector('.wl-eng__plate-cap');
      this.ptexts = [this.querySelector('.wl-eng__ptext-1'), this.querySelector('.wl-eng__ptext-2')];

      // 入力欄（中央 / 上段・下段）
      this.centerInput = this.querySelector('.wl-eng__text--1 [data-eng-text]');
      var t2 = this.querySelectorAll('.wl-eng__text--2 [data-eng-text]');
      this.upperInput = t2[0] || null;
      this.lowerInput = t2[1] || null;

      // 測定用の隠し要素
      this.meas = document.createElement('span');
      this.meas.setAttribute('aria-hidden', 'true');
      var s = this.meas.style;
      s.position = 'absolute'; s.visibility = 'hidden'; s.whiteSpace = 'nowrap';
      s.left = '-9999px'; s.top = '0'; s.pointerEvents = 'none';
      this.appendChild(this.meas);

      this.MAX_WIDTH_PX = this.computeMaxWidth();

      var self = this;
      // ラジオ（書体・彫刻）変更
      Array.prototype.forEach.call(
        this.querySelectorAll('input[name="properties[彫刻]"], input[name="properties[書体]"]'),
        function (r) { r.addEventListener('change', function () { self.update(); }); }
      );
      // 文字入力（幅制限＋プレビュー）
      [this.centerInput, this.upperInput, this.lowerInput].forEach(function (inp) {
        if (!inp) return;
        inp.removeAttribute('maxlength'); // 幅ベース制限に置き換え
        ['input', 'compositionend', 'keyup'].forEach(function (ev) {
          inp.addEventListener(ev, function () { self.render(); });
        });
      });

      this.update();
      // レイアウト確定後にもう一度（初回は plate 幅が未確定で文字が小さくなるのを防ぐ）
      if (window.requestAnimationFrame) requestAnimationFrame(function () { requestAnimationFrame(function () { self.render(); }); });

      // Webフォント読込後に上限を再計算して再描画
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          self.MAX_WIDTH_PX = self.computeMaxWidth();
          self.render();
        });
      }
      window.addEventListener('resize', function () { self.render(); });
    }

    currentFont() {
      var c = this.querySelector('input[name="properties[書体]"]:checked');
      return c && c.value === 'Dancing Script' ? 'script' : 'sans';
    }

    currentRows() {
      var c = this.querySelector('input[name="properties[彫刻]"]:checked');
      return c ? (parseInt(c.getAttribute('data-rows'), 10) || 0) : 0;
    }

    measureWidth(str, f) {
      this.meas.style.fontFamily = fontStack(f);
      this.meas.style.fontSize = SETTINGS.BASE_FONT_PX + 'px';
      this.meas.textContent = str;
      return this.meas.getBoundingClientRect().width;
    }

    computeMaxWidth() {
      var max = 0;
      var fonts = ['sans', 'script'];
      for (var i = 0; i < fonts.length; i++) {
        for (var j = 0; j < SETTINGS.REFERENCE_STRINGS.length; j++) {
          var w = this.measureWidth(SETTINGS.REFERENCE_STRINGS[j], fonts[i]);
          if (w > max) max = w;
        }
      }
      return max * SETTINGS.SAFETY_FACTOR;
    }

    clampToWidth(str, f) {
      if (this.measureWidth(str, f) <= this.MAX_WIDTH_PX) return str;
      var lo = 0, hi = str.length, fit = '';
      while (lo <= hi) {
        var mid = Math.floor((lo + hi) / 2);
        var sub = str.slice(0, mid);
        if (this.measureWidth(sub, f) <= this.MAX_WIDTH_PX) { fit = sub; lo = mid + 1; }
        else { hi = mid - 1; }
      }
      return fit;
    }

    enforceLimit(input, f) {
      if (!input) return '';
      var v = input.value;
      if (this.measureWidth(v, f) <= this.MAX_WIDTH_PX) return v;
      var clamped = this.clampToWidth(v, f);
      input.value = clamped;
      try { input.setSelectionRange(clamped.length, clamped.length); } catch (e) {}
      input.classList.add('is-limit');
      var self = this;
      clearTimeout(this._limitTimer);
      this._limitTimer = setTimeout(function () {
        if (self.centerInput) self.centerInput.classList.remove('is-limit');
        if (self.upperInput) self.upperInput.classList.remove('is-limit');
        if (self.lowerInput) self.lowerInput.classList.remove('is-limit');
      }, 1400);
      return clamped;
    }

    // ラジオ変更時：入力欄の出し分け・料金ID同期・プレビュー更新
    update() {
      var rows = this.currentRows();
      this.setBlock(this.text1, rows === 1);
      this.setBlock(this.text2, rows === 2);

      var checked = this.querySelector('input[name="properties[彫刻]"]:checked');
      var fee = checked ? (checked.getAttribute('data-fee') || '0') : '0';
      if (this.feeInput) {
        if (fee && fee !== '0') { this.feeInput.value = fee; this.feeInput.disabled = false; }
        else { this.feeInput.value = ''; this.feeInput.disabled = true; }
      }

      this.render();
    }

    setBlock(el, active) {
      if (!el) return;
      el.hidden = !active;
      var inputs = el.querySelectorAll('[data-eng-text]');
      for (var i = 0; i < inputs.length; i++) inputs[i].disabled = !active;
    }

    render() {
      if (!this.preview || !this.plate) return;
      var rows = this.currentRows();

      // 彫刻なし → プレビュー非表示
      if (rows === 0) { this.preview.hidden = true; return; }
      this.preview.hidden = false;

      var f = this.currentFont();
      var diameter = this.plate.clientWidth;
      if (!diameter || diameter < 120) diameter = Math.min(200, Math.round((window.innerWidth || 375) * 0.62));
      var lines = rows === 2 ? [this.upperInput, this.lowerInput] : [this.centerInput];

      // 各行の値（幅制限を適用）
      var vals = [];
      for (var k = 0; k < rows; k++) vals[k] = this.enforceLimit(lines[k], f);

      // 表示フォント：各行を独立に、その行が最大幅いっぱいになるよう拡大（1行目は2行目に影響されない）
      var usableW = diameter * 0.86;
      var capFont = rows === 2 ? diameter * 0.28 : diameter * 0.54;

      if (this.cap) {
        // 注意書きは固定文言（ユーザー入力を含まない）ため innerHTML で改行
        this.cap.innerHTML = (rows === 2
          ? '彫刻イメージ（上段・下段がこの範囲に収まる長さまで）'
          : '彫刻イメージ（この範囲に収まる長さまで）')
          + '<br>表示は仕上がりのイメージです。実物とは文字の大きさやバランスが多少異なる場合がございます';
      }

      for (var i = 0; i < 2; i++) {
        var pt = this.ptexts[i];
        if (!pt) continue;
        if (i >= rows) { pt.hidden = true; continue; }
        pt.hidden = false;
        var v = vals[i];
        pt.style.fontFamily = fontStack(f);
        if (v && v.length) {
          var w0 = this.measureWidth(v, f);
          pt.style.fontSize = (w0 > 0 ? Math.min(SETTINGS.BASE_FONT_PX * usableW / w0, capFont) : capFont) + 'px';
          pt.textContent = v;
          pt.classList.remove('wl-eng__ptext--empty');
        } else {
          pt.style.fontSize = '13px';
          pt.textContent = rows === 2 ? (i === 0 ? '上段' : '下段') : 'ここに文字が刻まれます';
          pt.classList.add('wl-eng__ptext--empty');
        }
      }
    }
  }

  if (!customElements.get('welume-engraving')) {
    customElements.define('welume-engraving', WeLumeEngraving);
  }
})();
